/**
 * Anthropic Data Source - v13 Section 6.10 (Sprint 11g)
 *
 * Direct fetcher for Anthropic's model list.
 * These are the REAL model IDs that the Anthropic API accepts.
 *
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║  NO HARDCODED MODEL DATA - All model info comes from Anthropic API           ║
 * ║                                                                              ║
 * ║  Anthropic API provides: model ID, display_name, created_at                  ║
 * ║  Anthropic API does NOT provide: pricing, context window, max completion     ║
 * ║  These should be enriched from OpenRouter metadata                           ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 *
 * @see https://docs.anthropic.com/en/api/models
 */
/**
 * Create display name from model data
 * Prefer API-provided display_name, fall back to generic cleanup
 */
const createDisplayName = (model) => {
    if (model.display_name) {
        return model.display_name;
    }
    // Fallback: generic cleanup
    return model.id
        .replace(/-/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};
/**
 * Filter for chat models (exclude any non-chat model types)
 * This is TYPE-based filtering, not model-name filtering
 */
const isChatModel = (modelId) => {
    const id = modelId.toLowerCase();
    // Exclude non-chat model TYPES (embeddings, etc.)
    if (id.includes('embed'))
        return false;
    return true;
};
/**
 * Fetch models directly from Anthropic API
 * Returns only API-provided data - no hardcoded pricing or limits
 *
 * @param apiKey - Anthropic API key
 * @returns Array of ModelMetadata for Anthropic models (with null for missing fields)
 */
export const fetchFromAnthropic = async (apiKey) => {
    const response = await fetch('https://api.anthropic.com/v1/models', {
        headers: {
            'x-api-key': apiKey,
            'anthropic-version': '2023-06-01',
            'anthropic-dangerous-direct-browser-access': 'true',
            'Content-Type': 'application/json',
        },
    });
    if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`Anthropic API returned ${response.status}: ${text || response.statusText}`);
    }
    const data = await response.json();
    if (!data.data || !Array.isArray(data.data)) {
        throw new Error('Invalid response format from Anthropic API');
    }
    const models = [];
    for (const model of data.data) {
        // Skip non-chat models (type-based filtering)
        if (!isChatModel(model.id))
            continue;
        // Use only API-provided data - null for missing fields
        models.push({
            id: model.id,
            provider: 'anthropic',
            displayName: createDisplayName(model),
            contextWindow: null, // API doesn't provide this - enrich from OpenRouter
            maxCompletionTokens: null, // API doesn't provide this - enrich from OpenRouter
            pricing: null, // API doesn't provide pricing - enrich from OpenRouter
            capabilities: [], // API doesn't provide this
            zeroDataRetention: null, // Unknown for Anthropic
            isReasoningModel: null, // API doesn't provide this - enrich from OpenRouter
        });
    }
    return models;
};
